<template>
  <Dialog v-model:visible="visible" maximizable position="top" :style="{ width: width }" header="Recipie Details"
    :modal="true" class="p-fluid recipie-dialog">
    <div class="p-field">
      <InputText id="name" v-model.trim="recipie.name" required="true" placeholder="Name" autofocus />
    </div>

    <div class="p-field mb-3">
      <InputCategory v-model="recipie.category_ids" type="Recipie" placeholder="Categories" :multiple="true" />
    </div>

    <SelectButton v-model="tab" :options="tabOptions" class="mb-3" optionLabel="label" optionValue="value" />

    <!-- Ingredients -->
    <template v-if="tab == 'ingredients'">
      <div class="ingredients my-4 py-2">
        <div class="d-flex justify-content-between my-3">
          <div class="fw-bold">
            <span>Ingredients for</span>
            <InputNumber v-model="recipie.people_count" class="d-inline-block w-auto mx-2" inputStyle="width: 4rem"
              inputClass="text-center" />
            <span>People</span>
          </div>
          <div class="fw-bold my-auto">
            <span>{{ recipiePrice }} € for 1 person </span>
            <i v-if="missingProductPrices && missingProductPrices.length > 0" class="pi pi-exclamation-triangle"
              v-tooltip="missingProductPrices" type="text"></i>
          </div>
        </div>
        <div v-for="(product, index) in recipie.products" class="d-flex mb-2 align-items-center product-row"
          :key="index" draggable="true" :data-index="index" @dragstart="onProductDragStart($event, index)"
          @dragend="onProductDragEnd" @dragover.prevent="onProductDragOver($event, index)"
          @dragleave="onProductDragLeave" @drop="onProductDrop($event, index)">

          <div class="p-inputgroup">
            <span class="p-inputgroup-addon drag-handle" title="Drag to reorder">
              <i class="pi pi-bars" />
            </span>
            <InputProduct v-model="product.id" :showClear="false" :editable="true" style="border-radius: 0;" />
            <InputNumber v-model="product.amount" :maxFractionDigits="5" placeholder="Amount"
              inputClass="border-start-0 input-amount" />
            <span class="p-inputgroup-addon" style="width: 5rem;">{{ $root.getProduct(product.id).unit }}</span>
            <span v-if="getProductPricePerPerson(product) !== null" class="p-inputgroup-addon" style="width: 6rem;">
              {{ getProductPricePerPerson(product) }} €
            </span>
            <span v-else class="p-inputgroup-addon" style="width: 6rem;"></span>
          </div>
          <Button icon="pi pi-times" class="p-button-text p-button-danger" @click="removeProduct(product)" />
        </div>
        <Button icon="pi pi-plus" class="p-button-primary p-button-sm w-auto mt-2" label="Ingredient"
          @click="recipie.products.push({})"></Button>
      </div>
    </template>
    <!-- Preparation Instructions Day Before -->
    <template v-if="tab == 'instructions_day_before'">
      <div class="my-4 py-2">
        <Editor v-model="recipie.instructions_day_before" api-key="gld93ttf4tgy3id80qeozfn2kkussyi011pcw85aile0k2z9"
          :init="editorConfig" />
        <div class="p-field mt-3">
          <label class="mb-2 d-block">Products to list on day before</label>
          <MultiSelect v-model="recipie.products_to_list_on_day_before" :options="dayBeforeProductOptions"
            optionLabel="name" optionValue="id" placeholder="All recipe products" class="w-100" />
        </div>
      </div>
    </template>
    <!-- Preparation Instructions -->
    <template v-if="tab == 'instructions'">
      <div class="my-4 py-2">
        <Editor v-model="recipie.instructions" api-key="gld93ttf4tgy3id80qeozfn2kkussyi011pcw85aile0k2z9"
          :init="editorConfig" />
      </div>
    </template>
    <!-- Timeline Day Before -->
    <template v-if="tab == 'timeline_day_before'">
      <div class="my-4 py-2 align-items-center timeline-container">
        <div v-for="step in recipie.timeline_day_before" class="d-flex mb-2" :key="step">
          <input type="time" class="p-inputtext w-auto" placeholder="Time" v-model="step.time" />
          <Editor v-model="step.text" api-key="gld93ttf4tgy3id80qeozfn2kkussyi011pcw85aile0k2z9" :init="editorConfig" />
          <Button icon="pi pi-times" class="p-button-text p-button-danger" @click="removeStepDayBefore(step)" />
        </div>
        <Button icon="pi pi-plus" class="p-button-primary p-button-sm w-auto mt-2" label="Step"
          @click="recipie.timeline_day_before.push({})"></Button>
      </div>
    </template>
    <!-- Timeline -->
    <template v-if="tab == 'timeline'">
      <div class="my-4 py-2 align-items-center timeline-container">
        <div v-for="step in recipie.timeline" class="d-flex mb-2" :key="step">
          <input type="time" class="p-inputtext w-auto" placeholder="Time" v-model="step.time" />
          <Editor v-model="step.text" api-key="gld93ttf4tgy3id80qeozfn2kkussyi011pcw85aile0k2z9" :init="editorConfig" />
          <Button icon="pi pi-times" class="p-button-text p-button-danger" @click="removeStep(step)" />
        </div>
        <Button icon="pi pi-plus" class="p-button-primary p-button-sm w-auto mt-2" label="Step"
          @click="recipie.timeline.push({})"></Button>
      </div>
    </template>
    <!-- Leftovers -->
    <template v-if="tab == 'leftovers'">
      <div class="my-4 py-2">
        <Editor v-model="recipie.leftovers_instructions" api-key="gld93ttf4tgy3id80qeozfn2kkussyi011pcw85aile0k2z9"
          :init="editorConfig" />
      </div>
    </template>
    <div class="p-field-checkbox">
      <Checkbox id="day_before" v-model="recipie.prepare_day_before" :binary="true" />
      <label for="day_before" class="ms-2">Need to be prepared the day before</label>
    </div>

    <template #footer>
      <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="visible = false" />
      <Button label="Save" icon="pi pi-check" class="p-button-text" :loading="loading" @click="saveRecipie" />
    </template>
  </Dialog>
</template>

<script>

import InputNumber from 'primevue/inputnumber'
import Checkbox from 'primevue/checkbox'
import Editor from '@tinymce/tinymce-vue'
import MultiSelect from 'primevue/multiselect'
import SelectButton from 'primevue/selectbutton'
import InputProduct from '@/components/InputProduct.vue'
import InputCategory from '@/components/InputCategory.vue'

export default {
  components: {
    InputNumber, InputProduct, Checkbox, InputCategory, Editor, MultiSelect, SelectButton,
  },
  data() {
    return {
      visible: false,
      loading: false,
      recipie: {},
      tab: 'ingredients',
      productDragIndex: null,
      editorConfig: {
        menubar: false,
        plugins: 'advlist anchor autolink charmap image insertdatetime link lists media preview searchreplace table emoticons code',
        toolbar: 'bold italic underline | forecolor backcolor | removeformat | alignleft aligncenter alignright | bullist numlist outdent indent | link image emoticons blockquote | code',
        invalid_styles: { '*': 'font-size,font-family' }
      }
    }
  },
  computed: {
    width() {
      return this.tab === 'ingredients' ? '700px' : '1100px'
    },
    tabOptions() {
      if (this.recipie.prepare_day_before) {
        return [
          { value: 'ingredients', label: 'Ingredients' },
          { value: 'instructions_day_before', label: 'Instr. Day Before' },
          { value: 'instructions', label: 'Instructions' },
          { value: 'timeline_day_before', label: 'Timeline Day Before' },
          { value: 'timeline', label: 'Timeline' },
          { value: 'leftovers', label: 'Leftovers' },
        ]
      }
      return [
        { value: 'ingredients', label: 'Ingredients' },
        { value: 'instructions', label: 'Instructions' },
        { value: 'timeline', label: 'Timeline' },
        { value: 'leftovers', label: 'Leftovers' },
      ]
    },
    recipiePrice() {
      return this.$root.getRecipiePrice(this.recipie.id)
    },
    missingProductPrices() {
      return this.$root.getRecipieMissingProductPrices(this.recipie.id)
    },
    dayBeforeProductOptions() {
      const ids = new Set()
      return (this.recipie.products || [])
        .filter((product) => product?.id && !ids.has(product.id) && ids.add(product.id))
        .map((product) => ({
          id: product.id,
          name: this.$root.getProduct(product.id).name || `Product #${product.id}`
        }))
    },
  },
  methods: {
    show(object = {}) {
      this.recipie = { ...object }
      this.recipie.products ||= [{}]
      this.recipie.products_to_list_on_day_before ||= []
      this.recipie.timeline ||= [{}]
      this.recipie.timeline.sort((a, b) => new Date(`1970-01-01T${a.time || '00:00'}:00`) - new Date(`1970-01-01T${b.time || '00:00'}:00`))
      this.recipie.timeline_day_before ||= [{}]
      this.recipie.timeline_day_before.sort((a, b) => new Date(`1970-01-01T${a.time || '00:00'}:00`) - new Date(`1970-01-01T${b.time || '00:00'}:00`))
      this.recipie.people_count ||= 10
      this.visible = true
    },
    async saveRecipie() {
      if (this.recipie.name) {
        // filter empty ingredients
        this.recipie.products = this.recipie.products.filter((p) => p.id && p.amount)
        this.recipie.products_to_list_on_day_before = (this.recipie.products_to_list_on_day_before || [])
          .filter((productId) => this.recipie.products.some((p) => p.id === productId))

        if (this.recipie.id) {
          this.dbUpdate('recipies', this.recipie)
        } else {
          const newRecipie = await this.dbCreate('recipies', this.recipie)
          this.$emit('created', newRecipie)
        }
        this.visible = false
        this.recipie = {}
      }
    },
    onProductDragStart(event, index) {
      this.productDragIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', index)
      event.target.classList.add('dragging')
    },
    onProductDragEnd() {
      this.productDragIndex = null
      document.querySelectorAll('.product-row.dragging, .product-row.drag-over').forEach((el) => {
        el.classList.remove('dragging', 'drag-over')
      })
    },
    onProductDragOver(event, index) {
      if (this.productDragIndex !== index) event.currentTarget.classList.add('drag-over')
    },
    onProductDragLeave(event) {
      event.currentTarget.classList.remove('drag-over')
    },
    onProductDrop(event, dropIndex) {
      event.currentTarget.classList.remove('drag-over')
      const dragIndex = this.productDragIndex
      if (dragIndex == null || dragIndex === dropIndex) return
      const item = this.recipie.products.splice(dragIndex, 1)[0]
      this.recipie.products.splice(dropIndex, 0, item)
      this.productDragIndex = null
    },
    getProductPricePerPerson(product) {
      const totalPrice = this.$root.computePrice(product.amount, product.id)
      const peopleCount = Number(this.recipie.people_count)

      if (totalPrice === null || !peopleCount) return null

      return (Number(totalPrice) / peopleCount).toFixed(2)
    },
    removeProduct(product) {
      this.recipie.products = this.recipie.products.filter((p) => p !== product)
      this.recipie.products_to_list_on_day_before = (this.recipie.products_to_list_on_day_before || [])
        .filter((productId) => this.recipie.products.some((p) => p.id === productId))
    },
    removeStep(step) {
      this.recipie.timeline = this.recipie.timeline.filter((s) => s.time !== step.time)
    },
    removeStepDayBefore(step) {
      this.recipie.timeline_day_before = this.recipie.timeline_day_before.filter((s) => s.time !== step.time)
    },
  },
}
</script>

<style lang="scss">
.ingredients {
  .product-row {
    cursor: grab;

    &.dragging {
      opacity: 0.5;
    }

    &.drag-over {
      outline: 2px solid var(--primary-color);
      outline-offset: 2px;
    }

    .drag-handle {
      cursor: grab;

      &:hover {
        color: var(--primary-color);
      }
    }
  }

  .input-product-wrapper {
    width: 20% !important;
  }

  .p-inputgroup {
    &:hover>.btn-edit-product {
      border-color: var(--primary-color);
    }

    .btn-edit-product {
      border-radius: 0 !important;
      background-color: transparent;
      border-color: #ced4da;
      border-left: none !important;
      width: 2.5rem !important;
      justify-content: flex-start;
      padding-left: .25rem !important;
      color: var(--text-color);

      .pi {
        font-size: .9rem;
      }

      &:hover {
        background-color: transparent !important;
        color: var(--primary-color);
        border-color: #ced4da;
      }
    }
  }

  .input-product {
    border-right: none;
  }
}

.timeline-container {
  .p-calendar {
    width: 12rem;
  }

  .tox-tinymce {
    height: 150px !important;
    width: 100% !important;
    margin-left: 1rem;
  }
}
</style>
